// What each check-in day is worth — shared by the Rewards page (the cards and
// tiles) and the daily popup on the bottom nav. Both used to derive this on
// their own, and the popup fell behind: it only read the legacy `checkin`
// config while the Rewards page had long moved to the split engine (V102), so
// the popup could announce a check-in the Rewards page no longer showed.
// One resolver, two readers.
import type { ActivityConfig, CheckinDay } from '../../features/profile/api';
import type { MessageKey } from '../i18n/messages';
import { fmtAmount, fmtInt, fmtMoney } from '../lib/format.ts';

export type SplitCheckinDay = {
  day: number;
  rewards?: Array<{ kind?: string; amount?: number; ticket_count?: number; coupon_code?: string; coupon_bonus_type?: string; coupon_bonus_value?: number }>;
};

export type CheckinReward = NonNullable<SplitCheckinDay['rewards']>[number];

// Flatten a split-config day onto the flat CheckinDay the tiles render. A day
// can grant several things at once (day 3 of the deposit track is a coupon AND
// 3 tickets); the tile shows the headline one — cash first, then tickets, then
// a coupon — and marks the rest with a "+" so nothing is silently dropped.
export type RichCheckinDay = CheckinDay & { extra?: boolean; all?: SplitCheckinDay['rewards'] };

export function normalizeSplitCheckinDays(days: SplitCheckinDay[]): RichCheckinDay[] {
  return [...days]
    .sort((a, b) => a.day - b.day)
    .map((entry) => {
      // A day is either nested (rewards[]) or flat (kind/amount on the day
      // itself) — and a single track may legally mix both. Treat a flat day
      // as a one-reward list so every day flattens the same way; without
      // this, a nested day arriving via the legacy tracks[] path rendered
      // R$ 0 tiles and an em-dash payoff.
      const flat = entry as SplitCheckinDay & { kind?: string; amount?: number; ticket_count?: number; coupon_code?: string; coupon_bonus_type?: string; coupon_bonus_value?: number };
      const rewards = entry.rewards && entry.rewards.length
        ? entry.rewards
        : (flat.kind || flat.amount || flat.ticket_count || flat.coupon_code)
          ? [{ kind: flat.kind, amount: flat.amount, ticket_count: flat.ticket_count, coupon_code: flat.coupon_code, coupon_bonus_type: flat.coupon_bonus_type, coupon_bonus_value: flat.coupon_bonus_value }]
          : [];
      const primary =
        rewards.find((r) => r.kind === 'cash' && Number(r.amount) > 0)
        || rewards.find((r) => Number(r.ticket_count) > 0)
        || rewards.find((r) => !!r.coupon_code)
        || rewards[0];
      return {
        day: entry.day,
        kind: primary?.kind,
        amount: primary?.amount,
        ticket_count: primary?.ticket_count,
        coupon_code: primary?.coupon_code,
        coupon_bonus_type: primary?.coupon_bonus_type,
        coupon_bonus_value: primary?.coupon_bonus_value,
        extra: rewards.length > 1,
        all: rewards,
      };
    });
}

// track → its 7 days, from the activity configs the summary endpoint returns.
// Legacy `checkin.config.tracks[]` first; then the split engine (V102):
// `checkin_{track}` supersedes the legacy entry for that track, and a DISABLED
// split config suppresses the track outright rather than falling back to the
// legacy figures — otherwise a decommissioned check-in would be the only one
// players saw while the two actually running stayed invisible.
export function resolveCheckinTrackDays(configs: ActivityConfig[] | undefined): Map<string, RichCheckinDay[]> {
  const all = configs || [];
  const legacy = all.find((cfg) => cfg.activityType === 'checkin');
  const map = new Map<string, RichCheckinDay[]>();
  // A disabled legacy config contributes nothing — same rule as a disabled
  // split track below.
  ((legacy && legacy.enabled !== false ? legacy.config?.tracks : undefined) || []).forEach((tr) => {
    // Same normalization as the split engine: legacy tracks may carry
    // nested rewards[] days too (composite coupon+ticket days do).
    map.set(tr.track, normalizeSplitCheckinDays((tr.days || []) as SplitCheckinDay[]));
  });
  for (const track of ['login', 'bet', 'deposit']) {
    const split = all.find((cfg) => cfg.activityType === `checkin_${track}`);
    if (!split) continue;
    if (!split.enabled) { map.delete(track); continue; }
    const days = (split.config as { days?: SplitCheckinDay[] } | undefined)?.days;
    if (Array.isArray(days)) map.set(track, normalizeSplitCheckinDays(days));
  }
  return map;
}

// A track with nothing behind any of its days is hidden everywhere — no card,
// no popup. Matches the Rewards page's "全 0 轨隐藏" rule.
export function checkinTrackFunded(days: RichCheckinDay[] | undefined): boolean {
  return (days || []).some((d) => Number(d.amount) > 0 || Number(d.ticket_count) > 0 || !!d.coupon_code);
}

export function checkinDayRewards(day: RichCheckinDay | undefined): CheckinReward[] {
  if (!day) return [];
  if (day.all?.length) return day.all;
  return [{ kind: day.kind, amount: day.amount, ticket_count: day.ticket_count, coupon_code: day.coupon_code, coupon_bonus_type: day.coupon_bonus_type, coupon_bonus_value: day.coupon_bonus_value }];
}

// "R$ 3 + 2 bilhetes" — the payoff spelled out. A tile can only afford a glyph;
// the headline can afford words, and words are what a player understands.
export function checkinRewardText(rewards: CheckinReward[], t: (key: MessageKey) => string) {
  const cash = rewards.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const tickets = rewards.reduce((sum, r) => sum + Number(r.ticket_count || 0), 0);
  const coupons = rewards.filter((r) => !!r.coupon_code);
  const parts: string[] = [];
  if (cash > 0) parts.push(fmtMoney(cash));
  if (tickets > 0) parts.push(t(tickets === 1 ? 'activitiesTicketOne' : 'activitiesTicketsN').replace('%n%', fmtInt(tickets)));
  // Coupons are listed by face value, never summed: an R$ 10 and an R$ 30
  // coupon are two separate deposit offers, not one worth R$ 40.
  const valued = coupons.map((reward) => couponValueLabel(reward)).filter(Boolean);
  valued.forEach((value) => parts.push(t('activitiesCouponWorth').replace('%v%', value!)));
  const unknown = coupons.length - valued.length;
  if (unknown > 0) {
    parts.push(t(unknown === 1 ? 'activitiesCouponOne' : 'activitiesCouponsN').replace('%n%', fmtInt(unknown)));
  }
  return parts.join(' + ');
}

// The 40px tile: one figure and a glyph. A bare ticket glyph told a player
// nothing about what the day is worth, so each tile carries its real number;
// the full sentence rides on the title attribute for anyone who needs it.
//
// `compact` drops the currency symbol: the popup strip has ~38px per tile and
// its headline already names the currency, so "0,50" beats a truncated "R$ 0…".
export function checkinTileLabel(d: RichCheckinDay, opts: { compact?: boolean } = {}): string {
  const money = opts.compact ? fmtAmount : fmtMoney;
  const base = d.kind === 'raffle_ticket'
    ? `${Number(d.ticket_count || 0)}\u{1F39F}`
    : d.kind === 'coupon'
      ? (couponValueLabel(d, opts.compact) ? `${couponValueLabel(d, opts.compact)}\u{1F3AB}` : '\u{1F3AB}')
      : money(Number(d.amount || 0));
  return d.extra ? `${base}+` : base;
}

function couponValueLabel(reward: CheckinReward, compact = false): string | undefined {
  const value = Number(reward.coupon_bonus_value);
  if (!Number.isFinite(value) || value <= 0) return undefined;
  if (reward.coupon_bonus_type === 'percent') return `${Number((value * 100).toFixed(2))}%`;
  if (reward.coupon_bonus_type === 'fixed') return (compact ? fmtAmount : fmtMoney)(value);
  return undefined;
}
